/**
 * PORT (wide screens): the original's popups on a phone. The game draws its 807x600 window as tall as the screen, so a
 * phone in landscape shows it at ~0.69 CSS px per stage px (a 320 CSS px tall one at ~0.53): the popups' 11-12 px body
 * text measured 7-8 CSS px and their buttons (the red close arrow: 19 CSS px) were a fingertip's size or less, while the
 * popups used two thirds of the screen's height and half its width. With a wide host (Stage.visibleRect: phones,
 * tablets, the app) each popup of the game (a screen over the popups' dim, menu_bg: new_interf.init) is
 *  - shown larger (`zoom`): scaled round its own middle as far as the shown safe area holds it with a margin, and at
 *    most until its body text measures BODY_CSS (a tablet, where it does already: not at all), moved in where it
 *    would leave the shown area; the game's own slide in and out (FWP.animation_y) goes on in its scale. Its taps
 *    map through the scale like its art (no input path changes); the game reads a slider's finger in the screen's own
 *    coordinates (new_interf.slider_proceed). Its scale does not depend on a field of it having the focus (a tap that
 *    only takes the focus away moves nothing under the finger); only while the host shows a focused field above an
 *    on-screen keyboard (Stage.$keyboardView) the scale stays as it was;
 *  - over the HUD of the screen under it (the main map's plates, the battle's groups: src/game/ui/wide-screen.ts):
 *    the HUD's groups it covers are hidden while it is open (under the dim they only showed round its edges and under
 *    its close arrow), but for those a page of the tutorial is about;
 *  - a finger's target: each button of it whose art measures less than HIT_CSS CSS px takes the taps of a rectangle
 *    of HIT_CSS round it (its hit state: SimpleButton.hitTestState), as far as the room to the other controls allows
 *    (split half way between two of them, a tie's band at the middle neither's), never over a text field one types into.
 * The tutorial (menu_view_tutorial) is a popup whose pages hold the dim themselves: page 1-2 a flat dim over the window
 * (shape 156), pages 3-6 one shape of a dim with a spotlight cut out and the page's panel on it (tools/extract/lib/
 * wide/tutorial.mjs). On a wide screen a dim of the window's size left the margin beside it light, with seams at the
 * window's edges, and the player's plate had moved out of its spotlight: here one flat dim of the same colour covers
 * everything shown (drawn in the tutorial's own coordinates, under its panel), with the page's spotlights where the
 * objects are now (the plate's level box and bars as the HUD places them, the arsenal, the shop), the page's panel
 * is the extension art without the dim, and the page is shown larger clear of what it points at.
 * Without a wide host (a desktop) nothing of this happens: the popups are the original's.
 */
import { Event, MouseEvent, Point, Rectangle, Sprite, TextFormat, flashRuntime } from '@flash';
import { HIT_SKIP_INVISIBLE, HIT_SKIP_MASK } from '@flash/display/DisplayObject';
import { toTwips } from '@flash/utils/numeric';
import { BEANSTALK, STALK_GAP } from './controls';
import { LIST_CONTAINERS, WideLists } from './wide-lists';
import { WideFingers } from './wide-fingers';
import { WideTabs } from './wide-tabs';
import { battleOwnsPopup, shownRect } from './battle-results';

/** A rectangle in stage (or a screen's) coordinates. */
export interface Area {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** The popups' body text (px as the original draws it) and what it measures at most once shown larger (CSS px). */
export const BODY_PX = 12;
export const BODY_CSS = 15;
/** Room between a popup shown larger and the shown safe area's edges (CSS px). */
export const MARGIN_CSS = 6;
/** The touch target the popups' buttons grow towards (CSS px each side). */
export const HIT_CSS = 48;
/** Room kept between a button's grown hit area and another control (CSS px). */
const HIT_GAP_CSS = 2;
/**
 * Between two buttons that both grow, a band this wide (CSS px) at the middle is neither's: a tap there is as near to
 * one as to the other, and the touch input takes such a tap for a tie (distances less than TOUCH_TIE_PX apart,
 * browser-input.ts): none pressed, as between two close buttons without targets of their own.
 */
const HIT_SPLIT_CSS = 2;
/** The step a popup's scale is lowered by while it would cover what it must keep clear of. */
const ZOOM_STEP = 0.05;
/** How often (frames) the tab screens' hit areas are looked at again. */
const HIT_FRAMES = 6;

/**
 * Popups that only tell something (a result, a notice, a help text): a tap on the dim round them closes them as their
 * close arrow does (every popup's `close` only removes it). Not the popups where something is chosen or typed (the
 * settings, a question), nor the victory's (its close skips the search of the graves).
 */
const DISMISS = new Set(['end_game_lose', 'end_game_new_lvl', 'what_opened', 'grave_result', 'roul result', 'error', 'rating', 'how_tribute_calcs', 'auc_rules', 'info', 'tired', 'spec_learn_more', 'papa_john']);
/**
 * Popups the game opens without the popups' dim (bg_blur), with a dim of their own over the screen under them: a
 * player's card from the tops (user_info, over the tab).
 */
const OWN_DIM = new Set(['user_info']);
/** The close button of the tab screens, the bar and the shop (the red arrow at the window's corner). */
const CORNER_CLOSE = 'close_to_mf';
/** What the corner's close arrow measures at least (its larger side, CSS px), its target, its room from the edges. */
const CORNER_ART_CSS = 44;
const CORNER_HIT_CSS = 60;
const CORNER_MARGIN_CSS = 8;

/**
 * growTexts: what a text of the screens over the main map measures on a phone once grown (CSS px), the largest size it
 * grows to (px as the original draws it), the largest original size that grows (body text; headings stay).
 */
const TEXT_CSS = 12;
const TEXT_MAX = 18;
const TEXT_GROW_FROM = 12;
/**
 * What a grown text keeps (px of its field's parent): at least this much room from the edges drawn round it (a card's
 * frame, a panel's border) and from the window's sides where the wide screen clips it (or what it had at the game's
 * size where that was less: SIDE_KEEP across, SIDE_KEEP_Y above and below), and ART_GAP from the art beside it.
 */
const SIDE_KEEP = 5;
const SIDE_KEEP_Y = 4;
const ART_GAP = 3;
/**
 * The same field of a list's rows (the arsenal's items, the tops' rows) takes the size this share of its rows holds;
 * the few longer ones (a long item's name) the largest size each holds itself.
 */
const LIST_SHARE = 0.9;
/** How many frames running a text measured before the art round it was decoded is measured again at most. */
const UNSURE_RUNS = 60;
/** A list of so many rows and more (fewer: one size, the smallest any of them holds). */
const LIST_ROWS = 3;
/**
 * The screens whose background shape draws the window's outline round their panel (the clans: shape 962, 1 px of
 * dark at the window's edges): on a wide host the map beyond the window shows round it, and the outline would run
 * across the map (through it, shown larger): it is cut off (OUTLINE_CUT px at each side).
 */
const WINDOW_OUTLINES: Record<string, number> = { clans: 962 };
const OUTLINE_CUT = 2;
/** The record of a text growTexts looks at (WidePopups.grown). */
interface Grown {
  base: number;
  size: number;
  fit: number;
  fitFor: number;
  text: string;
  w: number;
  h: number;
  line: boolean;
  ver: number;
}
/** The screens whose texts stay as the original sizes them (the main map's and the battle's HUD, the popups' dim). */
const NO_GROW = new Set(['mainframe', 'game', 'bg_blur']);

/** The tutorial's page dims (black, the shapes' alpha 177/255) and their spotlights. */
const TUTORIAL_DIM = { color: 0, alpha: 177 / 255 };
/** Page 1-2's dim over the window (a flat shape). */
const TUTORIAL_FLAT = 156;
/**
 * Pages 3-6: the page's shape (a dim with its spotlight and the panel) and the extension art of its panel alone; the
 * spotlights (in the original's window: the shape's own hole) and what the page keeps clear of when shown larger:
 *  - `plate`: holes in the main map's coordinates round the player's plate's level box and its bars, where the HUD's
 *    `plate` group puts them (their place as the original drew them, moved and scaled with the group);
 *  - `path`: a hole in stage coordinates ('M/Q/Z' of the shape's own outline: the arsenal, the shop stay where they
 *    are).
 * `keep`: HUD groups (their names) the page is about: shown, not covered.
 */
interface TutorialPage {
  shape: number;
  art: string;
  plate?: Area[];
  path?: string;
  keep?: string[];
}

const TUTORIAL_PAGES: Record<number, TutorialPage> = {
  3: {
    shape: 181,
    art: 'wide_tutorial_3',
    // shape 181's two holes: the level box (64, 11)-(108.05, 31) and the bars (76.85, 44.75)-(196.2, 71.4)
    plate: [
      { x: 64, y: 11, width: 44.05, height: 20 },
      { x: 76.85, y: 44.75, width: 119.35, height: 26.65 },
    ],
    keep: ['plate'],
  },
  4: {
    shape: 186,
    art: 'wide_tutorial_4',
    // shape 186's hole round the arsenal (the outline between its dim and nothing, a quadratic curve or a line per edge)
    path:
      'M 234.05 524.55 L 234.3 526.8 Q 234.95 532.8 232.6 538.3 Q 231.25 541.4 228.9 544.35 L 227.65 545.9 Q 208.6 569 183 579.6 ' +
      'Q 177.9 581.7 172.6 583.3 L 165.35 585.3 Q 137.7 592.15 114.7 586.1 Q 90.85 585.25 78.85 566.5 Q 76.4 564.75 73.15 563.2 ' +
      'Q 65.95 559.75 59.05 555.95 Q 44.8 548.1 31.8 538.9 Q 23.25 532.8 22.15 521.1 L 21.7 515.45 Q 20 490.25 24.65 464.75 ' +
      'L 26.35 456.25 L 26.4 455.9 Q 29.9 440.05 45.5 437.15 L 52 436.5 L 56.6 436.8 Q 96.7 439.6 130.6 456.5 ' +
      'Q 154.4 468.35 180.75 473.55 Q 224.8 482.3 232.95 518.55 L 234.05 524.55 Z',
  },
  5: {
    shape: 191,
    art: 'wide_tutorial_5',
    // shape 191's hole round the shop (its own outline, a quadratic curve per edge)
    path:
      'M 689.55 299.1 Q 740.7 291.5 765.6 332.2 Q 784.25 362.65 777.9 397.75 Q 768.5 449.6 760.9 500.25 Q 760.4 503.4 757.5 507 ' +
      'Q 725.75 546.35 679 561.6 Q 610.15 584.1 541.85 554.6 Q 504.25 538.35 515.4 500.2 Q 523 474.1 512.5 454.25 ' +
      'Q 508.85 447.3 503.9 440.85 Q 483.95 414.95 499.55 381.05 Q 503.15 373.15 503.3 363.75 Q 503.95 326.3 533.85 306.95 ' +
      'Q 539.95 303 548.45 302.75 Q 573.75 301.95 594.95 288 Q 634.8 294.9 673.8 299.4 Q 681.6 300.3 689.55 299.1 Z',
  },
  6: { shape: 196, art: 'wide_tutorial_6' },
};
/** Page 2 (gold and diamonds) is about the gold plate: shown, not covered. */
const TUTORIAL_KEEP: Record<number, string[]> = { 2: ['balance'] };

function inflate(r: Area, d: number): Area {
  return { x: r.x - d, y: r.y - d, width: r.width + 2 * d, height: r.height + 2 * d };
}

function overlaps(a: Area, b: Area): boolean {
  return a.x < b.x + b.width && b.x < a.x + a.width && a.y < b.y + b.height && b.y < a.y + a.height;
}

/** Whether `a` holds the whole of `b`. */
function holds(a: Area, b: Area): boolean {
  return a.x <= b.x && a.y <= b.y && b.x + b.width <= a.x + a.width && b.y + b.height <= a.y + a.height;
}

function union(a: Area | null, b: Area): Area {
  if (!a) return { ...b };
  const x = Math.min(a.x, b.x);
  const y = Math.min(a.y, b.y);
  return { x, y, width: Math.max(a.x + a.width, b.x + b.width) - x, height: Math.max(a.y + a.height, b.y + b.height) - y };
}

/**
 * Where a popup whose content is `content` (its own coordinates as the original places it) is shown larger in `view`
 * (the shown safe area, stage coordinates) at `cssScale` CSS px per stage px: its scale `k` (1: as it is) and the
 * position of its origin (x, y), its middle where it was, moved in from the view's edges, and clear of `keep` (stage
 * rectangles it must not cover: a smaller scale, a place beside them). `max` limits the scale further.
 */
export function fitArea(content: Area, view: Area, cssScale: number, keep: Area[] = [], max = Infinity): { k: number; x: number; y: number } {
  const none = { k: 1, x: 0, y: 0 };
  if (!(content.width > 0 && content.height > 0 && view.width > 0 && view.height > 0 && cssScale > 0)) return none;
  const m = MARGIN_CSS / cssScale;
  const inner = { x: view.x + m, y: view.y + m, width: view.width - 2 * m, height: view.height - 2 * m };
  const top = Math.min(max, BODY_CSS / (BODY_PX * cssScale), inner.width / content.width, inner.height / content.height);
  const walls = keep.map((r) => inflate(r, m));
  // (at its own size where it measures enough already, moved only when it would cover what it must keep clear of)
  if (!(top > 1.001) && !walls.some((r) => overlaps(r, content))) return none;
  const cx = content.x + content.width / 2;
  const cy = content.y + content.height / 2;
  const clamp = (v: number, lo: number, hi: number) => (hi < lo ? (lo + hi) / 2 : Math.min(Math.max(v, lo), hi));
  for (let k = Math.max(1, top); k >= 1 - 1e-9; k = k - ZOOM_STEP < 1 && k > 1 ? 1 : k - ZOOM_STEP) {
    const w = content.width * k;
    const h = content.height * k;
    const place = (x: number, y: number): Area => ({ x: clamp(x, inner.x, inner.x + inner.width - w), y: clamp(y, inner.y, inner.y + inner.height - h), width: w, height: h });
    const first = place(cx - w / 2, cy - h / 2);
    const tries = [first];
    for (const r of walls) tries.push(place(r.x - w, first.y), place(r.x + r.width, first.y), place(first.x, r.y - h), place(first.x, r.y + r.height));
    const ok = tries.find((t) => !walls.some((r) => overlaps(t, r)));
    if (ok) return { k, x: ok.x - content.x * k, y: ok.y - content.y * k };
  }
  return none;
}

/** A popup of the game shown larger: its scale and place, and what the game set (its slide: y). */
interface Zoomed {
  link: any;
  name: string;
  /** Its content as the original places it (its own coordinates), null until measured. */
  content: Area | null;
  k: number;
  x: number;
  y: number;
  /** The x / y the game gave it (0, or its slide's offset), and the x / y written last (a change is the game's). */
  gx: number;
  gy: number;
  wx: number;
  wy: number;
  /** The shown safe area and scale it was fitted to (null: to be fitted). */
  fitted: string | null;
  /** Its buttons with a hit area of their own: the art's own hit state. */
  hits: Map<any, any>;
  hitKey: string;
  /** Its children beyond the window that it hid (shown larger, they would come into view). */
  beyond: Set<any>;
  /**
   * Its children the screen's dressing clips to the window (src/game/ui/wide-screen.ts: a popup with a dim of its own,
   * OWN_DIM) that it lets out while shown larger: the frame reaches beyond the window then.
   */
  unclipped: Set<any>;
  /** Its background shape whose outline of the window it cut off (WINDOW_OUTLINES) and the place the game gave it. */
  cut: { o: any; x: number; y: number } | null;
  /** The tutorial's page dressed last (0: none), its dim and the art of its panel, the page's own dims it hid. */
  page: number;
  dims: Set<any>;
  dim: any;
  art: any;
  dimKey: string;
}

/** What a text field one types into measures at least with its pad (focusPads; CSS px). */
const FIELD_CSS = 44;
/** A link's grown hit area (setHits: a transparent child under its own children). */
const LINK_HIT = '$wide_link_hit';

/** The HUD groups a popup hid (stage children named `$wide_hud_<group>` of the screens under it). */
const HUD_PREFIX = '$wide_hud_';

export class WidePopups {
  private readonly zoomed: Zoomed[] = [];
  private readonly hiddenHud = new Set<any>();
  /** The close arrows moved to the screen's corner: the transform, clip and hit state the original gave them. */
  private readonly corners = new Map<any, { x: number; y: number; sx: number; sy: number; clip: boolean; hit: any; key: string; px: number; py: number }>();
  /** The corner's close arrows of screens under a popup, hidden while it is open. */
  private readonly coveredCorners = new Set<any>();
  /** The popups' dims that close an informational popup when tapped (their listeners). */
  private readonly dims = new WeakSet<any>();

  /**
   * @param fwp the game's root (its interface: fwp._interf, the screen stack cur_screen)
   * @param stage the stage (visibleRect, safeRect, cssScale, RESIZE)
   * @param art the bundles that hold the extension art (the tutorial's panels: interf)
   */
  constructor(
    private readonly fwp: any,
    private readonly stage: any,
    private readonly art: () => Array<{ has(n: string): boolean; get_object(n: string): any }>,
  ) {
    this.lists = new WideLists(stage, () => this.fwp && this.fwp._interf);
    this.fingers = new WideFingers(stage);
    this.tabsLayout = new WideTabs(stage);
    fwp.addEventListener(Event.EXIT_FRAME, this.onFrame);
    stage.addEventListener(Event.RESIZE, this.onResize);
  }

  /** The rows of the screens' lists (src/game/ui/wide-lists.ts). */
  private readonly lists: WideLists;
  /** The roulette's bets and the dropdown filters for fingers (src/game/ui/wide-fingers.ts). */
  private readonly fingers: WideFingers;
  /** The tab screens laid out anew on a phone (src/game/ui/wide-tabs.ts). */
  private readonly tabsLayout: WideTabs;

  destroy(): void {
    this.fwp.removeEventListener(Event.EXIT_FRAME, this.onFrame);
    this.stage.removeEventListener(Event.RESIZE, this.onResize);
    this.zoomed.length = 0;
  }

  /** The popups shown now (tests, diagnostics): their names and scales. */
  get popups(): Array<{ name: string; k: number }> {
    return this.zoomed.filter((z) => z.link.stage).map((z) => ({ name: z.name, k: z.k }));
  }

  /** A wide host shows the stage (a phone, a tablet, the app): the popups are dressed. */
  private on(): boolean {
    return !!this.stage.$visibleArea && this.stage.cssScale > 0;
  }

  private readonly onResize = (): void => {
    for (const z of this.zoomed) z.fitted = null;
    this.onFrame();
  };

  /** The game's popups now: screens of the stack over the popups' dim (bg_blur) or with a dim of their own (OWN_DIM). */
  private openPopups(): Array<{ link: any; name: string }> {
    const i = this.fwp && this.fwp._interf;
    const c = i && i.cur_screen;
    const n = c ? Number(c[0]) || 0 : 0;
    const out: Array<{ link: any; name: string }> = [];
    for (let k = 2; k <= n; k++) if (c[k] && c[k - 1] && (c[k - 1].name === 'bg_blur' || OWN_DIM.has(String(c[k].name))) && c[k].link) out.push({ link: c[k].link, name: String(c[k].name) });
    return out;
  }

  private readonly onFrame = (): void => {
    const open = this.openPopups();
    // The battle's own popups (results, the graves' search, the weapon sheet, the scanner) are laid out larger by
    // ui/battle-results.ts and its kin: here they only hide the HUD under them and close with a tap on their dim.
    const own = open.filter((o) => !battleOwnsPopup(this.fwp, o.name));
    // Popups that closed are forgotten (a screen is built anew each time it opens).
    for (let i = this.zoomed.length - 1; i >= 0; i--) if (!own.some((o) => o.link === this.zoomed[i].link)) this.zoomed.splice(i, 1);
    const on = this.on();
    for (const o of own) {
      let z = this.zoomed.find((q) => q.link === o.link);
      if (!z) {
        z = { link: o.link, name: o.name, content: null, k: 1, x: 0, y: 0, gx: o.link.x, gy: o.link.y, wx: o.link.x, wy: o.link.y, fitted: null, hits: new Map(), hitKey: '', beyond: new Set(), unclipped: new Set(), cut: null, page: 0, dims: new Set(), dim: null, art: null, dimKey: '' };
        this.zoomed.push(z);
      }
      if (on) this.dressTutorial(z);
      else if (z.page) WidePopups.undressTutorial(z);
      this.place(z, on);
      if (on) this.setHits(z);
    }
    this.hideHud(open, on);
    this.placeCorners(own, on);
    if (on) this.dismissable(open);
    // (the tabs' new fields first: the texts grow in them)
    this.tabsLayout.update(this.screenEntries(), on);
    this.growTexts(on);
    this.tabHits(own, on);
    this.lists.update(this.screens(), on);
    this.fingers.update(this.screens(), on);
    this.focusPads(on);
  };

  /** The text fields one types into with a pad of their own (focusPads): the pad and the layout it was drawn for. */
  private readonly pads = new Map<any, { pad: any; key: string }>();
  private padFrame = 0;

  /**
   * A text field one types into whose box measures less than FIELD_CSS CSS px tall (the online lobby's room code, the
   * clans' search, the worm names) takes the taps of a band FIELD_CSS tall round it (a little wider), as far as the
   * other controls leave room (half way to another field): a transparent pad under it that gives it the focus (the
   * on-screen keyboard opens as for a tap on the field). Without a wide host there are none.
   */
  private focusPads(on: boolean): void {
    // (every few frames, as the tab screens' hit areas: a field moves rarely)
    if (on && this.pads.size && ++this.padFrame % HIT_FRAMES !== 0) return;
    const css = this.stage.cssScale;
    const live = new Set<any>();
    if (on && css > 0) {
      const fields: any[] = [];
      const controls: any[] = [];
      const walk = (o: any, depth: number): void => {
        if (!o || !o.visible) return;
        if (o.$renderKind === 'text') {
          if (o.type === 'input') fields.push(o);
          return;
        }
        if (o.$isButton || o.buttonMode) {
          controls.push(o);
          return;
        }
        if (depth > 4 || typeof o.numChildren !== 'number' || o.mouseChildren === false || (typeof o.name === 'string' && o.name.startsWith('$'))) return;
        for (let j = 0; j < o.numChildren; j++) walk(o.getChildAt(j), depth + 1);
      };
      for (const link of this.screens()) walk(link, 0);
      const T = FIELD_CSS / css;
      const gap = HIT_GAP_CSS / css;
      const rect = (o: any): Area | null => {
        const b = o.getBounds(this.stage);
        if (!(b.width > 0 && b.height > 0)) return null;
        let r: Area = { x: b.x, y: b.y, width: b.width, height: b.height };
        // (a button's grown hit area counts: it keeps its taps)
        const h = o.$isButton ? o.hitTestState : null;
        if (h && h.name === '$wide_hit') {
          const l = h.getBounds(h);
          const p0 = o.localToGlobal(new Point(l.x, l.y));
          const p1 = o.localToGlobal(new Point(l.x + l.width, l.y + l.height));
          r = union(r, { x: Math.min(p0.x, p1.x), y: Math.min(p0.y, p1.y), width: Math.abs(p1.x - p0.x), height: Math.abs(p1.y - p0.y) });
        }
        return r;
      };
      const boxes = fields.map((f) => rect(f));
      fields.forEach((f, i) => {
        const own = boxes[i];
        if (!own || !f.stage || own.height >= T - 0.5) return;
        live.add(f);
        let x0 = own.x - gap * 2;
        let x1 = own.x + own.width + gap * 2;
        let y0 = own.y + own.height / 2 - T / 2;
        let y1 = y0 + T;
        const cut = (r: Area, half: boolean) => {
          if (!(x0 < r.x + r.width && r.x < x1 && y0 < r.y + r.height && r.y < y1)) return;
          if (r.y >= own.y + own.height) y1 = Math.min(y1, half ? (own.y + own.height + r.y) / 2 : r.y - gap);
          else if (r.y + r.height <= own.y) y0 = Math.max(y0, half ? (r.y + r.height + own.y) / 2 : r.y + r.height + gap);
          else if (r.x >= own.x + own.width) x1 = Math.min(x1, r.x - gap);
          else if (r.x + r.width <= own.x) x0 = Math.max(x0, r.x + r.width + gap);
        };
        for (const c of controls) {
          const r = rect(c);
          if (r) cut(r, false);
        }
        boxes.forEach((r, j) => {
          if (j !== i && r) cut(r, true);
        });
        y0 = Math.min(y0, own.y);
        y1 = Math.max(y1, own.y + own.height);
        const parent = f.parent;
        const key = `${[x0, y0, x1, y1].map((v) => Math.round(v * 10)).join(',')}|${parent.getChildIndex(f)}`;
        let p = this.pads.get(f);
        if (p && p.key === key && p.pad.parent === parent) return;
        if (!p) {
          const pad = new Sprite();
          pad.name = '$wide_focus';
          pad.addEventListener(MouseEvent.MOUSE_DOWN, () => {
            if (!f.stage || f.type !== 'input' || !this.on()) return;
            this.stage.focus = f;
            const n = String(f.text ?? '').length;
            if (typeof f.setSelection === 'function') f.setSelection(n, n);
          });
          p = { pad, key: '' };
          this.pads.set(f, p);
        }
        const q0 = parent.globalToLocal(new Point(x0, y0));
        const q1 = parent.globalToLocal(new Point(x1, y1));
        const g = p.pad.graphics;
        g.clear();
        g.beginFill(0, 0);
        g.drawRect(Math.min(q0.x, q1.x), Math.min(q0.y, q1.y), Math.abs(q1.x - q0.x), Math.abs(q1.y - q0.y));
        g.endFill();
        // (right under its field: the field keeps its own taps, the controls above keep theirs)
        if (p.pad.parent !== parent) parent.addChildAt(p.pad, parent.getChildIndex(f));
        else if (parent.getChildIndex(p.pad) !== parent.getChildIndex(f) - 1) parent.setChildIndex(p.pad, Math.max(0, parent.getChildIndex(f) - 1));
        p.key = key;
      });
    }
    for (const [f, p] of this.pads) {
      if (live.has(f)) continue;
      if (p.pad.parent) p.pad.parent.removeChild(p.pad);
      this.pads.delete(f);
    }
  }

  /** The game's screens on the stack: their links and names. */
  private screenEntries(): Array<{ link: any; name: string }> {
    const i = this.fwp && this.fwp._interf;
    const c = i && i.cur_screen;
    const n = c ? Number(c[0]) || 0 : 0;
    const out: Array<{ link: any; name: string }> = [];
    for (let k = 1; k <= n; k++) if (c[k] && c[k].link) out.push({ link: c[k].link, name: String(c[k].name) });
    return out;
  }

  /** The game's screens on the stack (not the main map's and the battle's HUD screens, not the popups' dim). */
  private screens(): any[] {
    const i = this.fwp && this.fwp._interf;
    const c = i && i.cur_screen;
    const n = c ? Number(c[0]) || 0 : 0;
    const out: any[] = [];
    for (let k = 1; k <= n; k++) {
      const name = String(c[k] && c[k].name);
      // (the battle's own popups are laid out larger by ui/battle-results.ts and its kin: not grown twice)
      if (c[k] && c[k].link && !NO_GROW.has(name) && !battleOwnsPopup(this.fwp, name)) out.push(c[k].link);
    }
    return out;
  }

  private tabFrame = 0;
  /** The tab screens' (not popups: the bar, the shop, the tabs) buttons with hit areas of their own (setHits). */
  private readonly tabs = new Map<any, { link: any; name: string; content: Area; hits: Map<any, any>; hitKey: string }>();

  /**
   * The buttons of the screens under the popups (the tab screens, the bar, the shop; not the main map's and the battle's
   * HUD, which lays out its own) take the taps of HIT_CSS round them as far as their neighbours leave room, as the
   * popups' do; their close arrow is the corner's (placeCorners).
   */
  private tabHits(open: Array<{ link: any; name: string }>, on: boolean): void {
    if (!on) {
      for (const t of this.tabs.values()) for (const [b, orig] of t.hits) restoreHit(b, orig);
      this.tabs.clear();
      return;
    }
    // (every few frames: the buttons of a screen move rarely, a walk of a long list every frame costs a phone)
    if (++this.tabFrame % HIT_FRAMES !== 0 && this.tabs.size) {
      for (const [link, t] of this.tabs) {
        if (link.stage) continue;
        for (const [b, orig] of t.hits) restoreHit(b, orig);
        this.tabs.delete(link);
      }
      return;
    }
    const i = this.fwp && this.fwp._interf;
    const c = i && i.cur_screen;
    const n = c ? Number(c[0]) || 0 : 0;
    const live = new Set<any>();
    for (let k = 1; k <= n; k++) {
      const e = c[k];
      if (!e || !e.link || NO_GROW.has(String(e.name)) || open.some((o) => o.link === e.link)) continue;
      live.add(e.link);
      let t = this.tabs.get(e.link);
      if (!t) {
        t = { link: e.link, name: String(e.name), content: { x: 0, y: 0, width: 807, height: 600 }, hits: new Map(), hitKey: '' };
        this.tabs.set(e.link, t);
      }
      if (on) this.setHits(t as any);
    }
    for (const [link, t] of this.tabs) {
      if (live.has(link) && on) continue;
      for (const [b, orig] of t.hits) restoreHit(b, orig);
      this.tabs.delete(link);
    }
  }

  /** The fields shown larger now (growTexts: given back their sizes without a phone's wide view). */
  private readonly grownFields = new Set<any>();
  /**
   * Text fields growTexts looked at: the size the game gave each (`base`) and the one shown (`size`), the largest size
   * that fits it for the target `fitFor` (`fit`), what was shown then (a change is the game's) and the field's version
   * then (`ver`: while it is the same, nothing of the field changed).
   */
  private readonly grown = new WeakMap<any, Grown>();
  /** The runtime's render version and the layout (scale, shown area) the texts were sized for (unchanged: nothing to do). */
  private growVersion = -1;
  private growKey = '';
  /**
   * A text was sized while the art round it was not decoded yet: sized again on the next frame (UNSURE_RUNS frames
   * running at most: art that is never drawn is taken as drawn all over its box), and how many frames ran so.
   */
  private growUnsure = false;
  private unsureRuns = 0;
  /** The shown texts of a parent and their boxes (lineMatesOf), for the versions of the parent and of its texts. */
  private readonly textBoxes = new WeakMap<any, { sig: string; list: Array<{ o: any; b: Area }> }>();

  /**
   * On a phone (a wide host showing less than a CSS px per stage px) the texts of the screens and popups over the main
   * map (not the map's HUD, not the battle's) grow where their field has room for it: a size a step at a time up to
   * what measures TEXT_CSS CSS px (at most TEXT_MAX), with one size in all of it (a text of several sizes stays as it
   * is). A word-wrapped paragraph as far as it still fits the field's height (no line more than it holds); a text of
   * fixed lines (a label, a header, a value, the original's hand-broken lines) as far as each line still fits the
   * field's width (less its gutter) and all of them its height, none wrapped anew. Either keeps clear of what bounds
   * it (roomOf): the edges drawn round it (a card's frame, a panel's border), the window's sides where the wide
   * screen clips it, the art beside it, the beanstalk's leaves. Texts of one kind take one size, the largest all of
   * them hold (sizesOf): a column of paragraphs (the skills' four descriptions), a row and a column of fixed lines the
   * game gave one size (a label and its value, a table's row), one template's copies, the labels of a column of
   * buttons of one width; the same field of a list's rows the size most rows hold, a longer item the largest it
   * holds itself; a heading is not smaller than the text right under it. Never smaller than the game's size. A text
   * the game writes anew (a render, a hover) grows again; without a wide host every text is the game's. Nothing is
   * looked at again while nothing was drawn anew (the runtime's render version), the layout is the same and the art
   * round the texts was decoded when they were measured.
   */
  private growTexts(on: boolean): void {
    // (while the host shows a field above an on-screen keyboard at another scale: the sizes stay)
    if (this.stage.$keyboardView) return;
    const i = this.fwp && this.fwp._interf;
    const c = i && i.cur_screen;
    const n = c ? Number(c[0]) || 0 : 0;
    const css = this.stage.cssScale;
    const phone = on && css > 0 && css < 0.99;
    // A field that left the stage (a closed popup; a tab screen the game shows again later grows again) gets the game's
    // size back and is not held; without a phone's wide view every grown field does, and nothing else is looked at.
    for (const tf of [...this.grownFields]) if (!phone || !tf.stage) this.sizeTo(tf, 0);
    if (!phone) {
      this.grownFields.clear();
      this.growVersion = -1;
      return;
    }
    const r = this.stage.safeRect;
    const key = `${css}|${r.x},${r.y},${r.width},${r.height}`;
    const runtime = flashRuntime();
    if (!this.growUnsure && runtime.renderVersion === this.growVersion && key === this.growKey) return;
    this.unsureRuns = this.growUnsure ? this.unsureRuns + 1 : 0;
    this.growUnsure = false;
    const fits = new Map<any, number>();
    const screenOf = new Map<any, any>();
    for (let k = 1; k <= n; k++) {
      const e = c[k];
      // (the battle's own popups size their texts in the battle's layer, ui/battle-sheet.ts and kin)
      if (!e || !e.link || NO_GROW.has(String(e.name)) || battleOwnsPopup(this.fwp, String(e.name))) continue;
      const walk = (o: any, depth: number): void => {
        if (!o || !o.visible) return;
        if (o.$renderKind === 'text') {
          // (a field of a fixed size: one that grows with its text would reach over what lies under it; one a tab's
          // layout sizes itself, wide-tabs.ts)
          if (o.type !== 'input' && (!o.autoSize || o.autoSize === 'none') && !o.$wideTabText) {
            let k = 1;
            for (let p = o; p && p !== this.stage; p = p.parent) k *= Math.abs(p.scaleY || 1);
            const fit = this.fitOf(o, Math.min(TEXT_MAX, Math.ceil(TEXT_CSS / (css * k))), e.link);
            if (fit > 0) {
              fits.set(o, fit);
              screenOf.set(o, e.link);
            }
          }
          return;
        }
        // (the rows of the lists have their own layout: src/game/ui/wide-lists.ts)
        if (depth > 3 || typeof o.numChildren !== 'number' || (typeof o.name === 'string' && (o.name.startsWith('$') || LIST_CONTAINERS.has(o.name)))) return;
        for (let j = 0; j < o.numChildren; j++) walk(o.getChildAt(j), depth + 1);
      };
      walk(e.link, 0);
    }
    for (const [tf, px] of this.sizesOf(fits, screenOf)) this.sizeTo(tf, Math.max(px, this.baseOf(tf)));
    this.growVersion = runtime.renderVersion;
    this.growKey = key;
  }

  /**
   * The size of each text growTexts grows (`fits`: the largest each holds; `screenOf`: its screen): texts of one kind
   * take one size, the smallest of their fits (a mate that does not grow: its own size), each at least its own size as
   * the game gave it:
   *  - a fixed-line text's row and column mates of its size, a paragraph's column (lineMatesOf, columnOf), and theirs
   *    in turn;
   *  - the fixed-line texts of a parent of one style (bold or not, their alignment, the size the game gave them) in
   *    fields of one width and height that are not the labels of buttons (the headings of cards, their lines: one
   *    template wherever they lie);
   *  - the same field of a list's rows (one symbol's clips under one container: the tops' rows, the arsenal's items);
   *  - the labels of the buttons of one column (a button's own label or its caption over it: the same left edge and
   *    width, the same size as the game gave them);
   * the rows of a list of LIST_ROWS and more count with the size LIST_SHARE of them hold (a longer item's name: the
   * largest it holds itself, no larger than its kind); and a heading (one line right above a text, at least its size
   * as the game gave them) is not smaller than the text under it: that text's kind takes the heading's size at most.
   */
  private sizesOf(fits: Map<any, number>, screenOf: Map<any, any>): Map<any, number> {
    const fields = [...fits.keys()];
    const { root, pins, heads, list } = this.kindsOf(fields, screenOf);
    const index = new Map<any, number>(fields.map((f, i) => [f, i]));
    const px = new Map<number, number>();
    const rows = new Map<number, number[]>();
    fields.forEach((tf, i) => {
      const fit = fits.get(tf)!;
      if (list[i]) {
        const all = rows.get(root[i]);
        if (all) all.push(fit);
        else rows.set(root[i], [fit]);
      } else px.set(root[i], Math.min(px.get(root[i]) ?? Infinity, fit));
    });
    for (const [g, all] of rows) {
      all.sort((a, b) => b - a);
      px.set(g, Math.min(px.get(g) ?? Infinity, all[Math.max(0, Math.ceil(LIST_SHARE * all.length) - 1)]));
    }
    for (const [g, base] of pins) px.set(g, Math.min(px.get(g)!, base));
    const sizeOf = (tf: any) => {
      const j = index.get(tf);
      return j === undefined ? this.baseOf(tf) : Math.max(Math.min(px.get(root[j])!, fits.get(tf)!), this.baseOf(tf));
    };
    // A heading not smaller than the text under it (its kind; a heading's own size may be lowered in turn: a few rounds).
    for (let round = 0; round < 3 && heads.length; round++) {
      let changed = false;
      for (const [h, i] of heads) {
        const cap = sizeOf(h);
        if (px.get(root[i])! > cap) {
          px.set(root[i], cap);
          changed = true;
        }
      }
      if (!changed) break;
    }
    const out = new Map<any, number>();
    fields.forEach((tf, i) => out.set(tf, Math.min(px.get(root[i])!, fits.get(tf)!)));
    return out;
  }

  /** The kinds kindsOf found last, for the fields it looked at and what they were then (their texts, sizes, boxes). */
  private kinds: { fields: any[]; state: any[]; parents: Map<any, number>; root: number[]; pins: Array<[number, number]>; heads: Array<[any, number]>; list: boolean[] } | null = null;

  /**
   * The kinds of the texts sizesOf sizes (`fields`): each one's kind (the index of one of them), the sizes the kinds
   * take at most (a mate that does not grow), the headings over them, whether it is a row of a list of LIST_ROWS and
   * more (the same field of one symbol's clips under one container); found again only when a text, its size as the
   * game gave it, its box or the number of its parent's children changed (a frame of an animation elsewhere on the
   * screen changes none of it).
   */
  private kindsOf(fields: any[], screenOf: Map<any, any>): { root: number[]; pins: Array<[number, number]>; heads: Array<[any, number]>; list: boolean[] } {
    const state: any[] = [];
    const parents = new Map<any, number>();
    for (const tf of fields) {
      const g = this.grown.get(tf);
      state.push(g?.text, g?.base, g?.line, tf.x, tf.y, tf.width, tf.height);
      if (tf.parent) parents.set(tf.parent, tf.parent.numChildren);
    }
    const was = this.kinds;
    if (was && was.fields.length === fields.length && was.fields.every((f, i) => f === fields[i]) && was.state.every((v, i) => v === state[i]) && was.parents.size === parents.size && [...parents].every(([p, n]) => was.parents.get(p) === n)) return was;
    const index = new Map<any, number>(fields.map((f, i) => [f, i]));
    const up = fields.map((_, i) => i);
    const find = (i: number): number => {
      while (up[i] !== i) {
        up[i] = up[up[i]];
        i = up[i];
      }
      return i;
    };
    const join = (a: number, b: number) => {
      const ra = find(a);
      const rb = find(b);
      if (ra !== rb) up[ra] = rb;
    };
    // (mates that do not grow keep their kind at their own size)
    const pinned: Array<[number, number]> = [];
    const kinds = new Map<string, number>();
    const ids = new Map<any, number>();
    const idOf = (o: any) => {
      let v = ids.get(o);
      if (v === undefined) ids.set(o, (v = ids.size));
      return v;
    };
    const counts = new Map<string, number>();
    const rowOf: Array<string | null> = fields.map(() => null);
    const same = (kind: string, i: number) => {
      const j = kinds.get(kind);
      if (j === undefined) kinds.set(kind, i);
      else join(i, j);
      counts.set(kind, (counts.get(kind) ?? 0) + 1);
    };
    fields.forEach((tf, i) => {
      const base = this.baseOf(tf);
      const line = !!this.grown.get(tf)?.line;
      for (const m of line ? this.lineMatesOf(tf) : this.columnOf(tf)) {
        const j = index.get(m);
        if (j !== undefined) join(i, j);
        else pinned.push([i, this.baseOf(m)]);
      }
      const p = tf.parent;
      const btn = WidePopups.buttonOf(tf, screenOf.get(tf));
      // (fixed lines of one style and one field's box in a parent: one template, the cards' headings and lines; the
      // labels of buttons are of their column's kind)
      if (p && line && !btn) {
        const f = tf.getTextFormat(0, 1);
        same(`kind|${idOf(p)}|${base}|${f.bold === true}|${f.align}|${Math.round(tf.width)}|${Math.round(tf.height)}`, i);
      }
      const row = p && p.parent;
      const own = tf.$charId ?? (typeof tf.name === 'string' && !tf.name.startsWith('instance') ? tf.name : null);
      if (row && own != null && p.$charId != null) same((rowOf[i] = `row|${idOf(row)}|${p.$charId}|${own}|${base}`), i);
      if (btn) {
        const b = btn.getBounds(this.stage);
        same(`btn|${idOf(screenOf.get(tf))}|${Math.round(b.x)}|${Math.round(b.width)}|${base}`, i);
      }
    });
    const list = rowOf.map((k) => k !== null && (counts.get(k) ?? 0) >= LIST_ROWS);
    const root = fields.map((_, i) => find(i));
    const pins = pinned.map(([i, base]): [number, number] => [root[i], base]);
    const heads: Array<[any, number]> = [];
    fields.forEach((tf, i) => {
      const h = this.headingOver(tf);
      if (h) heads.push([h, i]);
    });
    this.kinds = { fields, state, parents, root, pins, heads, list };
    return this.kinds;
  }

  /**
   * The button a text is the label of: a button (or a clip with a hand cursor) it lies in within its screen, or a
   * sibling button its text's middle lies on, the only text there (a caption over its button: the port's BigButton,
   * the game's labels over their buttons; not a card with a heading and lines over it); null for none.
   */
  private static buttonOf(tf: any, screen: any): any {
    for (let p = tf.parent; p && p !== screen; p = p.parent) if (p.$isButton || p.buttonMode) return p;
    const p = tf.parent;
    if (!p || typeof p.numChildren !== 'number') return null;
    const ink = WidePopups.inkOf(tf);
    const cx = ink.x + ink.width / 2;
    const cy = ink.y + ink.height / 2;
    for (let j = 0; j < p.numChildren; j++) {
      const o = p.getChildAt(j);
      if (o === tf || !o.visible || !(o.$isButton || o.buttonMode) || (typeof o.name === 'string' && o.name.startsWith('$'))) continue;
      const b = o.getBounds(p);
      const on = (x: number, y: number) => x >= b.x && x <= b.x + b.width && y >= b.y && y <= b.y + b.height;
      if (!on(cx, cy)) continue;
      for (let q = 0; q < p.numChildren; q++) {
        const t = p.getChildAt(q);
        if (t === tf || t.$renderKind !== 'text' || !t.visible || !String(t.text ?? '').trim()) continue;
        const k = WidePopups.inkOf(t);
        if (k.width > 0 && on(k.x + k.width / 2, k.y + k.height / 2)) return null;
      }
      return o;
    }
    return null;
  }

  /**
   * The heading right above a text: a shown sibling of one line whose box ends at most its own height above the
   * text's (and not below the text's top), over half of the narrower one's width in common, larger as the game gave
   * them, or as large and bold over a text that is not; null for none.
   */
  private headingOver(tf: any): any {
    const p = tf.parent;
    if (!p || typeof p.numChildren !== 'number') return null;
    const list = this.textsOf(p);
    const me = list.find((e) => e.o === tf);
    if (!me) return null;
    const a = me.b;
    const base = this.baseOf(tf);
    if (!(base > 0)) return null;
    const bold = (o: any) => o.getTextFormat(0, 1).bold === true;
    const plain = !bold(tf);
    let best: any = null;
    let bestGap = Infinity;
    for (const { o, b } of list) {
      const hb = this.baseOf(o);
      if (o === tf || /[\r\n]/.test(String(o.text ?? '').trim()) || !(hb > base || (hb === base && plain && bold(o)))) continue;
      const gap = a.y - (b.y + b.height);
      const common = Math.min(a.x + a.width, b.x + b.width) - Math.max(a.x, b.x);
      if (gap < -2 || gap > b.height || b.y >= a.y || common < 0.5 * Math.min(a.width, b.width)) continue;
      if (gap < bestGap) {
        best = o;
        bestGap = gap;
      }
    }
    return best;
  }

  /** The shown texts of a parent and their boxes (in its coordinates), kept while it and its texts are unchanged. */
  private textsOf(p: any): Array<{ o: any; b: Area }> {
    let sum = 0;
    let count = 0;
    for (let j = 0; j < p.numChildren; j++) {
      const o = p.getChildAt(j);
      if (o.$renderKind !== 'text') continue;
      sum += Number(o.$version) || 0;
      count++;
    }
    const sig = `${Number(p.$version) || 0}:${count}:${sum}`;
    const was = this.textBoxes.get(p);
    if (was && was.sig === sig) return was.list;
    const list: Array<{ o: any; b: Area }> = [];
    for (let j = 0; j < p.numChildren; j++) {
      const o = p.getChildAt(j);
      // (not the texts of a block a tab's layout shows larger: wide-tabs.ts)
      if (o.$renderKind !== 'text' || !o.visible || o.$wideTabText || !String(o.text ?? '').trim()) continue;
      const b = o.getBounds(p);
      list.push({ o, b: { x: b.x, y: b.y, width: b.width, height: b.height } });
    }
    this.textBoxes.set(p, { sig, list });
    return list;
  }

  /**
   * The texts a text of fixed lines shares its size with: the shown texts of fixed lines of its size as the game gave
   * them beside it in its row (a label and its value, a table's row: their boxes level, half the smaller one's height
   * and more in common) and under it in its column (a list of items: the same left edge, a like width). None grows
   * where one cannot; a paragraph (the description under a heading) is of its own kind.
   */
  private lineMatesOf(tf: any): any[] {
    const p = tf.parent;
    if (!p || typeof p.numChildren !== 'number') return [];
    const list = this.textsOf(p);
    const me = list.find((e) => e.o === tf);
    if (!me) return [];
    const a = me.b;
    const base = this.baseOf(tf);
    const out: any[] = [];
    for (const { o, b } of list) {
      if (o === tf || this.baseOf(o) !== base || WidePopups.paragraph(o)) continue;
      const common = Math.min(a.y + a.height, b.y + b.height) - Math.max(a.y, b.y);
      const row = common >= 0.5 * Math.min(a.height, b.height) && Math.abs(a.y + a.height / 2 - (b.y + b.height / 2)) <= 4;
      const column = Math.abs(a.x - b.x) <= 4 && b.width > 0.8 * a.width && b.width < 1.25 * a.width;
      if (row || column) out.push(o);
    }
    return out;
  }

  /** The size the game gave a field (its own while it was never grown; 0: several sizes). */
  private baseOf(tf: any): number {
    const g = this.grown.get(tf);
    if (g) return g.base;
    const size = tf.getTextFormat().size;
    return typeof size === 'number' && size > 0 ? size : 0;
  }

  /**
   * The fields of a text's kind beside it (its column): shown siblings of several lines with the same left edge (4 px),
   * a like width and the same size as the game gave them.
   */
  private columnOf(tf: any): any[] {
    const p = tf.parent;
    const out: any[] = [];
    if (!p || typeof p.numChildren !== 'number') return out;
    const lines = (o: any) => String(o.text ?? '').trim().split(/\r\n|\r|\n/).length > 1 || o.numLines > 1;
    const base = this.baseOf(tf);
    for (let j = 0; j < p.numChildren; j++) {
      const o = p.getChildAt(j);
      if (o === tf || o.$renderKind !== 'text' || !o.visible || o.type === 'input' || o.$wideTabText || !String(o.text ?? '').trim()) continue;
      if (Math.abs(o.x - tf.x) > 4 || !(o.width > 0.8 * tf.width && o.width < 1.25 * tf.width) || this.baseOf(o) !== base || !lines(o)) continue;
      out.push(o);
    }
    return out;
  }

  /** The record of a field growTexts looks at (null: no text, or a text of several sizes). */
  private recordOf(tf: any): Grown | null {
    let g = this.grown.get(tf);
    // (nothing of the field changed since it was looked at: its text, its box, its size)
    if (g && g.ver === tf.$version) return g;
    const text = String(tf.text ?? '');
    // (the same text written anew as htmlText at the game's size: the shop's description at each render, e.g. when
    // its filter opens; the record's size is then no longer the field's)
    const now = tf.getTextFormat().size;
    if (g && !(text === g.text && tf.width === g.w && tf.height === g.h && now === g.size)) {
      // The game wrote anew: a text of its own sizes (an htmlText) is the game's again; one that kept the size grown
      // (a plain text) is judged again from the size the game gave it.
      if (now !== g.size) {
        this.grown.delete(tf);
        this.grownFields.delete(tf);
        g = undefined;
      } else {
        g.fitFor = -1;
        g.text = text;
        g.w = tf.width;
        g.h = tf.height;
      }
    }
    if (!g) {
      if (!text.trim()) return null;
      const size = tf.getTextFormat().size;
      if (typeof size !== 'number' || !(size > 0)) return null; // (several sizes)
      g = { base: size, size, fit: size, fitFor: -1, text, w: tf.width, h: tf.height, line: false, ver: -1 };
      this.grown.set(tf, g);
    }
    g.ver = tf.$version;
    return g;
  }

  /** Where a field's text is drawn (its lines' extent: alignment included), in its parent's coordinates. */
  private static inkOf(tf: any): Area {
    let x0 = Infinity;
    let x1 = -Infinity;
    for (let l = 0; l < tf.numLines; l++) {
      const m = WidePopups.lineSpan(tf, l);
      if (!(m.width > 0)) continue;
      x0 = Math.min(x0, m.x);
      x1 = Math.max(x1, m.x + m.width);
    }
    if (!(x1 > x0)) return { x: tf.x, y: tf.y, width: 0, height: 0 };
    const sx = tf.scaleX || 1;
    const sy = tf.scaleY || 1;
    return { x: tf.x + x0 * sx, y: tf.y + 2 * sy, width: (x1 - x0) * sx, height: tf.textHeight * sy };
  }

  /**
   * Whether art an object shows is not decoded yet (a shape's image not in its library: requested when first drawn),
   * so that what it draws is not known (a hit test takes it as drawn all over its box).
   */
  private static undecoded(o: any): boolean {
    const stack = [o];
    for (let n = 0; stack.length && n < 200; n++) {
      const q = stack.pop();
      if (!q.visible) continue;
      const img = q.$def && q.$def.image;
      if (img && q.$sym && typeof q.$sym.image === 'function' && !q.$sym.image(img.src)) return true;
      if (typeof q.$currentStateObject === 'function') {
        const st = q.$currentStateObject();
        if (st) stack.push(st);
      } else if (typeof q.numChildren === 'number') for (let j = 0; j < q.numChildren; j++) stack.push(q.getChildAt(j));
    }
    return false;
  }

  /** A word-wrapped paragraph: more lines than its text has (its own wrap broke them), not lines broken by hand. */
  private static paragraph(tf: any): boolean {
    return !!tf.wordWrap && tf.numLines > String(tf.text ?? '').split(/\r\n|\r|\n/).length;
  }

  /**
   * What a field's text keeps clear of when it grows (in its parent's coordinates; `ink0` its text at the game's size,
   * `paragraph` whether its lines wrap):
   *  - the edges drawn round it: of the decor whose box holds it (a card, a panel, the frame round a description), the
   *    box's edges where the text lies on what the decor draws, else on each side the first thing it draws beside the
   *    text (a divider; none where it draws nothing there: a shape of a frame elsewhere and a pager whose box reaches
   *    under a header); SIDE_KEEP from them across and SIDE_KEEP_Y above and below, or what it had where it had less;
   *  - the window's sides where the wide screen clips it to the window (as in the window's panel: the panel itself is
   *    no edge, the dressing carries it on beside the window), as far: a text clipped there would be cut;
   *  - the art beside it that it was clear of (an icon right after a label, "Ваш рейтинг: ?"): ART_GAP, or what it
   *    had;
   *  - the beanstalk's leaves of its screen: no closer than STALK_GAP (no further under them where it was already, as
   *    reflowArticle and clearOfBeanstalk keep the game's texts).
   * A paragraph's lines wrap inside its field (its gutter in): an edge across bounds it only where its field reaches
   * past what it keeps of it. The field's own box bounds a text of fixed lines by its width (fitOf).
   */
  private static roomOf(tf: any, ink0: Area, screen: any, paragraph: boolean): { room: (ink: Area) => boolean; unsure: boolean } {
    const p = tf.parent;
    const E = 0.01;
    const tests: Array<(k: Area) => boolean> = [];
    // (what a decor draws is known once its art is decoded, when it is first drawn: judged again then)
    let unsure = false;
    const own = tf.getBounds(p);
    const gutter = 2 * Math.abs(tf.scaleX || 1);
    const reach = { l: own.x + gutter, r: own.x + own.width - gutter };
    const r0 = ink0.x + ink0.width;
    const b0 = ink0.y + ink0.height;
    // Edges round the text (null: none on that side): kept as far as it had them, at most SIDE_KEEP / SIDE_KEEP_Y.
    const bound = (l: number | null, r: number | null, t: number | null, b: number | null) => {
      if (l !== null) {
        const lim = l + Math.min(SIDE_KEEP, ink0.x - l);
        if (!paragraph || reach.l < lim - E) tests.push((k) => k.x >= lim - E);
      }
      if (r !== null) {
        const lim = r - Math.min(SIDE_KEEP, r - r0);
        if (!paragraph || reach.r > lim + E) tests.push((k) => k.x + k.width <= lim + E);
      }
      if (t !== null) {
        const lim = t + Math.min(SIDE_KEEP_Y, ink0.y - t);
        tests.push((k) => k.y >= lim - E);
      }
      if (b !== null) {
        const lim = b - Math.min(SIDE_KEEP_Y, b - b0);
        tests.push((k) => k.y + k.height <= lim + E);
      }
    };
    if (p && typeof p.numChildren === 'number') {
      // (where the decor's drawing beside the text is looked for: as far as the text may grow, inside its field, and
      // the room it keeps beyond)
      const grow = Math.max(ink0.width, ink0.height) * (TEXT_MAX / Math.max(1, Number(tf.getTextFormat().size) || TEXT_MAX) - 1) + SIDE_KEEP + 1;
      const span = { l: Math.max(ink0.x - grow, own.x - SIDE_KEEP), r: Math.min(r0 + grow, own.x + own.width + SIDE_KEEP), t: own.y - SIDE_KEEP_Y, b: Math.min(b0 + grow, own.y + own.height + SIDE_KEEP_Y) };
      for (let j = 0; j < p.numChildren; j++) {
        const o = p.getChildAt(j);
        if (o === tf || !o.visible || o.$renderKind === 'text' || o.$wideOutlineCut || (typeof o.name === 'string' && o.name.startsWith('$'))) continue;
        const b = o.getBounds(p);
        // (not a panel over the whole window: the wide screen's dressing carries it on beside the window)
        if (!(b.width > 0 && b.height > 0) || (b.width >= 800 && b.height >= 590)) continue;
        const r = { x: b.x, y: b.y, width: b.width, height: b.height };
        if (holds(r, ink0)) {
          if (WidePopups.undecoded(o)) unsure = true;
          // (what it draws that is shown: hitTestPoint's shape test counts hidden parts too)
          const drawn = (x: number, y: number) => {
            const g = p.localToGlobal(new Point(x, y));
            return !!o.$hitTestShape(toTwips(g.x), toTwips(g.y), HIT_SKIP_INVISIBLE | HIT_SKIP_MASK);
          };
          const rows = [0.25, 0.5, 0.75].map((f) => ink0.y + f * ink0.height);
          const cols = [0.25, 0.5, 0.75].map((f) => ink0.x + f * ink0.width);
          // (on what it draws: its box's edges; else the first thing it draws beside the text on each side)
          if (cols.some((x) => drawn(x, ink0.y + ink0.height / 2))) bound(r.x, r.x + r.width, r.y, r.y + r.height);
          else {
            // (from the text's side outward a px at a time up to `end`: the inner edge of the first thing drawn)
            const scan = (from: number, end: number, hit: (v: number) => boolean): number | null => {
              const step = from < end ? 1 : -1;
              for (let v = from + step / 2; step > 0 ? v <= end : v >= end; v += step) if (hit(v)) return v - step / 2;
              return null;
            };
            bound(
              scan(ink0.x, Math.max(r.x, span.l), (x) => rows.some((y) => drawn(x, y))),
              scan(r0, Math.min(r.x + r.width, span.r), (x) => rows.some((y) => drawn(x, y))),
              scan(ink0.y, Math.max(r.y, span.t), (y) => cols.some((x) => drawn(x, y))),
              scan(b0, Math.min(r.y + r.height, span.b), (y) => cols.some((x) => drawn(x, y))),
            );
          }
        } else if (!overlaps(ink0, r)) {
          const dx = Math.max(0, r.x - r0, ink0.x - r.x - r.width);
          const dy = Math.max(0, r.y - b0, ink0.y - r.y - r.height);
          const wall = inflate(r, Math.min(ART_GAP, Math.max(dx, dy)) - E);
          tests.push((k) => !overlaps(k, wall));
        }
      }
    }
    // (a text inside the window that the wide screen clips to the window, it or a parent of it: between the window's
    // sides still, or it would be cut off; not one beyond them, the tops' hidden ids; a list's rows scroll past the
    // window's top and bottom)
    let clipped = false;
    for (let q = tf; q && q.parent; q = q.parent) if (q.$designClip) clipped = true;
    if (clipped && p) {
      const a = p.globalToLocal(new Point(0, 0));
      const b = p.globalToLocal(new Point(807, 600));
      const win = { x: Math.min(a.x, b.x), y: Math.min(a.y, b.y), width: Math.abs(b.x - a.x), height: Math.abs(b.y - a.y) };
      if (holds(win, ink0)) bound(win.x, win.x + win.width, null, null);
    }
    if (screen && typeof screen.numChildren === 'number' && p) {
      for (let j = 0; j < screen.numChildren; j++) {
        const s = screen.getChildAt(j);
        if (s.$charId !== BEANSTALK || !s.visible) continue;
        const b = s.getBounds(p);
        const limit = Math.max(r0, b.x - STALK_GAP);
        tests.push((k) => !(k.y < b.y + b.height && b.y < k.y + k.height) || k.x + k.width <= limit + E);
      }
    }
    return { room: (k) => tests.every((t) => t(k)), unsure };
  }

  /** The widest line of a field's text (its line metrics: the paragraph's own lines). */
  private static widestLine(tf: any): number {
    let w = 0;
    for (let l = 0; l < tf.numLines; l++) w = Math.max(w, WidePopups.lineSpan(tf, l).width);
    return w;
  }

  /**
   * Where a line of a field is drawn (its own px: its metrics' x and width, less the spaces it ends in, which a
   * left-aligned line's metrics count: a wrapped line's last space reaches past the field's gutter).
   */
  private static lineSpan(tf: any, l: number): { x: number; width: number } {
    const m = tf.getLineMetrics(l);
    const text = String(tf.getLineText(l) ?? '');
    const kept = text.replace(/\s+$/, '');
    if (!kept) return { x: m.x, width: 0 };
    if (kept.length === text.length) return { x: m.x, width: m.width };
    const b = tf.getCharBoundaries(tf.getLineOffset(l) + kept.length - 1);
    return { x: m.x, width: b ? Math.min(m.width, b.x + b.width - m.x) : m.width };
  }

  /**
   * The largest size up to `target` px that the field holds (its base where it does not grow; 0: not a text to grow);
   * `screen` the screen it is on.
   */
  private fitOf(tf: any, target: number, screen: any): number {
    const g = this.recordOf(tf);
    if (!g) return 0;
    if (g.fitFor === target) return g.fit;
    const shown = g.size;
    let best = g.base;
    if (g.size !== g.base) this.setSize(tf, g, g.base);
    // A paragraph (its lines wrapped at the game's size) grows as far as its field's height holds it; a text of fixed
    // lines (a label, a header, a value, the original's hand-broken lines) as far as each line still fits its box's
    // width and all of them its height, with no line wrapped anew (its row and column mates then take one size:
    // sizesOf); either as far as it keeps clear of what bounds it (roomOf).
    const paragraph = WidePopups.paragraph(tf);
    g.line = !paragraph;
    let unsure = false;
    if (target > g.base && g.base <= TEXT_GROW_FROM) {
      const ownW = tf.width / Math.abs(tf.scaleX || 1);
      const ownH = tf.height / Math.abs(tf.scaleY || 1);
      // (a field fitted with more room at its sides keeps it: controls.ts shrinkToFit's gutter)
      const gutter = Number(tf.$fitGutter) || 4;
      const lines0 = tf.numLines;
      const bounds = WidePopups.roomOf(tf, WidePopups.inkOf(tf), screen, paragraph);
      const room = bounds.room;
      unsure = bounds.unsure;
      const fitsBox = () => tf.maxScrollV <= 1 && tf.numLines === lines0 && WidePopups.widestLine(tf) <= ownW - gutter + 0.01 && tf.textHeight + 4 <= ownH + 0.5 && room(WidePopups.inkOf(tf));
      // (every line inside the field less its gutter, as the wrap keeps it: not a word longer than the field)
      const fitsWrapped = () => tf.maxScrollV <= 1 && WidePopups.widestLine(tf) <= ownW - 4 + 0.01 && room(WidePopups.inkOf(tf));
      if (paragraph || fitsBox()) {
        // (from the largest size its box could hold, as its lines scale with the size: a paragraph's height as its
        // area, with a px or two to spare for the wrap; a text of fixed lines its widest line and its height)
        const w0 = WidePopups.widestLine(tf);
        const h0 = tf.textHeight;
        const scale = paragraph ? Math.sqrt(Math.max(0, ownH - 4) / Math.max(1, h0)) : Math.min((ownW - gutter) / Math.max(1, w0), (ownH + 0.5 - 4) / Math.max(1, h0));
        const top = Math.min(target, Math.floor(g.base * scale) + (paragraph ? 2 : 1));
        for (let px = top; px > g.base; px--) {
          this.setSize(tf, g, px);
          if (paragraph ? fitsWrapped() : fitsBox()) {
            best = px;
            break;
          }
        }
      }
    }
    if (g.size !== shown) this.setSize(tf, g, shown);
    g.fit = best;
    // (measured again on the next frame while the art round it is not decoded yet)
    const again = unsure && this.unsureRuns < UNSURE_RUNS;
    g.fitFor = again ? -1 : target;
    if (again) this.growUnsure = true;
    return best;
  }

  /** Shows a field at `px` (0: the size the game gave it). */
  private sizeTo(tf: any, px: number): void {
    const g = this.recordOf(tf);
    if (!g) {
      this.grownFields.delete(tf);
      return;
    }
    const want = px > 0 ? px : g.base;
    if (g.size !== want) this.setSize(tf, g, want);
  }

  private setSize(tf: any, g: Grown, px: number): void {
    const f = new TextFormat();
    f.size = px;
    tf.setTextFormat(f);
    g.size = px;
    g.ver = tf.$version;
    if (px !== g.base) this.grownFields.add(tf);
    else this.grownFields.delete(tf);
  }

  /**
   * The close arrow of the tab screens, the bar and the shop (close_to_mf, at the window's corner in the middle of the
   * top edge of a wide screen, 19-27 CSS px) in the top right corner of the shown safe area, as large as CORNER_ART_CSS
   * and a target of CORNER_HIT_CSS: the corner of the screen, as a phone's close button is. Without a wide host it is
   * where the original has it.
   */
  private placeCorners(open: Array<{ link: any; name: string }>, on: boolean): void {
    const i = this.fwp && this.fwp._interf;
    const c = i && i.cur_screen;
    const n = c ? Number(c[0]) || 0 : 0;
    const shown = new Set<any>();
    const covered = new Set<any>();
    if (on) {
      // (the screen of the lowest popup: a tab's arrow under a popup is hidden, one red arrow on the screen)
      let popupAt = 0;
      for (let k = n; k >= 1; k--) if (c[k] && open.some((o) => o.link === c[k].link)) popupAt = k;
      for (let k = 1; k <= n; k++) {
        const link = c[k] && c[k].link;
        if (!link || open.some((o) => o.link === link) || typeof link.getChildByName !== 'function') continue;
        const b = link.getChildByName(CORNER_CLOSE);
        if (!b || !link.stage || !(b.visible || this.coveredCorners.has(b))) continue;
        if (popupAt && k < popupAt) covered.add(b);
        else shown.add(b);
      }
    }
    for (const b of this.coveredCorners) {
      if (covered.has(b)) continue;
      this.coveredCorners.delete(b);
      b.visible = true;
    }
    for (const b of covered) {
      this.coveredCorners.add(b);
      b.visible = false;
    }
    for (const [b, was] of this.corners) {
      if (shown.has(b)) continue;
      b.x = was.x;
      b.y = was.y;
      b.scaleX = was.sx;
      b.scaleY = was.sy;
      b.$designClip = was.clip;
      if (b.$isButton) b.hitTestState = was.hit;
      this.corners.delete(b);
    }
    const css = this.stage.cssScale;
    const r0 = this.stage.safeRect;
    const key = `${r0.x},${r0.y},${r0.width},${r0.height},${css}`;
    for (const b of shown) {
      let was = this.corners.get(b);
      if (was && was.key === key && b.x === was.px && b.y === was.py) continue; // (placed for this view already)
      if (!was) {
        was = { x: b.x, y: b.y, sx: b.scaleX, sy: b.scaleY, clip: !!b.$designClip, hit: b.$isButton ? b.hitTestState : null, key: '', px: 0, py: 0 };
        this.corners.set(b, was);
      }
      const parent = b.parent;
      // Its art at the original's scale (the parent's coordinates), then its scale and place at the corner.
      b.scaleX = was.sx;
      b.scaleY = was.sy;
      b.x = was.x;
      b.y = was.y;
      const own = b.getBounds(parent);
      const k = Math.min(3, Math.max(1, CORNER_ART_CSS / (Math.max(own.width, own.height) * css)));
      const r = this.stage.safeRect;
      const m = CORNER_MARGIN_CSS / css;
      // (the screen's coordinates are the stage's: a tab screen is never scaled)
      const p = parent.globalToLocal(new Point(r.x + r.width - m, r.y + m));
      b.scaleX = was.sx * k;
      b.scaleY = was.sy * k;
      const now = b.getBounds(parent);
      b.x += p.x - (now.x + now.width);
      b.y += p.y - now.y;
      b.$designClip = false;
      // On top of the screen's other children: nothing of it (a text over the window's corner) takes its taps.
      if (parent.getChildIndex(b) !== parent.numChildren - 1) parent.setChildIndex(b, parent.numChildren - 1);
      if (b.$isButton) {
        const art = b.getBounds(this.stage);
        const T = CORNER_HIT_CSS / css;
        let x1 = Math.min(r.x + r.width, art.x + art.width + (T - art.width) / 2);
        let x0 = x1 - T;
        let y0 = Math.max(r.y, art.y - (T - art.height) / 2);
        let y1 = y0 + T;
        // Clear of the screen's other controls (on a 4:3 tablet the corner is the window's, near its buttons).
        for (const o of WidePopups.controls(parent, b)) {
          if (!(x0 < o.x + o.width && o.x < x1 && y0 < o.y + o.height && o.y < y1)) continue;
          const left = o.x + o.width <= art.x ? o.x + o.width : null;
          const below = o.y >= art.y + art.height ? o.y : null;
          if (left !== null && (below === null || (x1 - left) * (y1 - y0) >= (below - y0) * (x1 - x0))) x0 = Math.max(x0, left);
          else if (below !== null) y1 = Math.min(y1, below);
        }
        const q0 = b.globalToLocal(new Point(x0, y0));
        const q1 = b.globalToLocal(new Point(x1, y1));
        const s = new Sprite();
        s.name = '$wide_hit';
        s.graphics.beginFill(0, 0);
        s.graphics.drawRect(Math.min(q0.x, q1.x), Math.min(q0.y, q1.y), Math.abs(q1.x - q0.x), Math.abs(q1.y - q0.y));
        s.graphics.endFill();
        b.hitTestState = s;
      }
      was.key = key;
      was.px = b.x;
      was.py = b.y;
    }
  }

  /** The screen's other controls (stage rectangles): buttons, clips with a hand cursor, fields one types into. */
  private static controls(screen: any, except: any): Area[] {
    const out: Area[] = [];
    const walk = (o: any, depth: number): void => {
      if (!o || !o.visible || o === except) return;
      if (o.$isButton || o.buttonMode || (o.$renderKind === 'text' && o.type === 'input')) {
        const b = o.getBounds(o.stage);
        if (b.width > 0 && b.height > 0) out.push({ x: b.x, y: b.y, width: b.width, height: b.height });
        return;
      }
      if (depth > 3 || typeof o.numChildren !== 'number' || o.mouseChildren === false) return;
      for (let i = 0; i < o.numChildren; i++) walk(o.getChildAt(i), depth + 1);
    };
    for (let i = 0; i < screen.numChildren; i++) walk(screen.getChildAt(i), 0);
    return out;
  }

  /** The dims under the informational popups (DISMISS) close them when tapped (their close arrow's click). */
  private dismissable(open: Array<{ link: any; name: string }>): void {
    const i = this.fwp && this.fwp._interf;
    const c = i && i.cur_screen;
    const n = c ? Number(c[0]) || 0 : 0;
    for (let k = 2; k <= n; k++) {
      const dim = c[k - 1] && c[k - 1].name === 'bg_blur' ? c[k - 1].link : null;
      if (!dim || this.dims.has(dim) || !open.some((o) => o.link === c[k].link) || !DISMISS.has(String(c[k].name))) continue;
      this.dims.add(dim);
      const popup = c[k].link;
      dim.addEventListener(MouseEvent.CLICK, () => {
        const top = i.cur_screen[i.cur_screen[0]];
        if (!this.on() || !top || top.link !== popup || this.fwp.animation) return;
        const close = typeof popup.getChildByName === 'function' ? popup.getChildByName('close') : null;
        if (close && close.visible) i.click({ target: close, currentTarget: close });
      });
    }
  }

  /**
   * The popup's content as the original places it (its own coordinates): its shown children that lie inside the
   * window (what lies beyond it the original never showed), not a dim over the whole window, not the port's parts.
   */
  private static content(link: any): Area | null {
    let r: Area | null = null;
    for (let i = 0; i < link.numChildren; i++) {
      const c = link.getChildAt(i);
      if (!c.visible || c.alpha <= 0) continue;
      // (the parts of this dressing: the tutorial's panel art counts, its dim and the hit areas do not)
      if (typeof c.name === 'string' && c.name.startsWith('$wide_') && !c.name.startsWith('$wide_wide_tutorial_')) continue;
      let b: any;
      try {
        b = c.getBounds(link);
      } catch {
        continue;
      }
      if (!(b.width > 0 && b.height > 0)) continue;
      if ((b.width >= 800 && b.height >= 590) || c.$wideOutlineCut) continue; // a dim over the whole window
      if (b.x < -0.5 || b.y < -0.5 || b.x + b.width > 807.5 || b.y + b.height > 600.5) continue;
      r = union(r, { x: b.x, y: b.y, width: b.width, height: b.height });
    }
    return r;
  }

  /** What the popup must keep clear of (stage rectangles): the tutorial's spotlights and the HUD its page is about. */
  private keepOf(z: Zoomed): Area[] {
    if (z.name !== 'view_tutorial') return [];
    const out: Area[] = [];
    const frame = Number(z.link.currentFrame) || 1;
    const page = TUTORIAL_PAGES[frame];
    for (const g of [...(page?.keep ?? []), ...(TUTORIAL_KEEP[frame] ?? [])]) {
      const s = this.hudGroup(g);
      if (s) out.push(this.stageBounds(s));
    }
    if (page?.path) out.push(pathBounds(page.path));
    return out;
  }

  private stageBounds(o: any): Area {
    const b = o.getBounds(this.stage);
    return { x: b.x, y: b.y, width: b.width, height: b.height };
  }

  /** The main map's HUD group `name` (the screen at the bottom of the stack), shown, or null. */
  private hudGroup(name: string): any {
    const i = this.fwp && this.fwp._interf;
    const c = i && i.cur_screen;
    const n = c ? Number(c[0]) || 0 : 0;
    for (let k = 1; k <= n; k++) {
      const link = c[k] && c[k].link;
      const s = link && typeof link.getChildByName === 'function' ? link.getChildByName(HUD_PREFIX + name) : null;
      if (s) return s;
    }
    return null;
  }

  /** Scales and places the popup (or gives it back its own place without a wide host); follows the game's slide. */
  private place(z: Zoomed, on: boolean): void {
    const link = z.link;
    // A change since the last frame is the game's (its slide sets y every frame of it).
    if (Math.abs(link.x - z.wx) > 0.001) z.gx = link.x;
    if (Math.abs(link.y - z.wy) > 0.001) z.gy = link.y;
    if (!on) {
      if (z.k !== 1 || z.x || z.y) {
        z.k = 1;
        z.x = z.y = 0;
      }
    } else if (!this.stage.$keyboardView) {
      const content = WidePopups.content(link);
      const r = this.stage.safeRect;
      const key = `${r.x},${r.y},${r.width},${r.height},${this.stage.cssScale}|${content ? [content.x, content.y, content.width, content.height].map((v) => Math.round(v)).join(',') : ''}|${link.currentFrame ?? ''}`;
      if (key !== z.fitted) {
        z.fitted = key;
        z.content = content;
        const f = content ? fitArea(content, { x: r.x, y: r.y, width: r.width, height: r.height }, this.stage.cssScale, this.keepOf(z)) : { k: 1, x: 0, y: 0 };
        z.k = f.k;
        z.x = f.x;
        z.y = f.y;
      }
    }
    // What the original put beyond its window to hide it (the tutorial's last page's "next page" above it) stays
    // hidden: a popup shown larger would bring it into view.
    for (let i = 0; i < link.numChildren; i++) {
      const c = link.getChildAt(i);
      if (typeof c.name === 'string' && c.name.startsWith('$wide_')) continue;
      const hidden = z.beyond.has(c);
      if (!c.visible && !hidden) continue;
      let b: any;
      try {
        // (bounds count hidden objects too)
        b = c.getBounds(link);
      } catch {
        continue;
      }
      const out = z.k > 1 && b.width > 0 && (b.x + b.width <= 0 || b.y + b.height <= 0 || b.x >= 807 || b.y >= 600);
      if (out && !hidden) {
        z.beyond.add(c);
        c.visible = false;
      } else if (!out && hidden) {
        z.beyond.delete(c);
        c.visible = true;
      }
    }
    for (let i = 0; i < link.numChildren; i++) {
      const c = link.getChildAt(i);
      if (z.k !== 1 && c.$designClip) {
        c.$designClip = false;
        z.unclipped.add(c);
      }
    }
    if (z.k === 1 && z.unclipped.size) {
      for (const c of z.unclipped) c.$designClip = true;
      z.unclipped.clear();
    }
    this.cutOutline(z, on);
    const x = z.x + z.k * z.gx;
    const y = z.y + z.k * z.gy;
    if (link.scaleX !== z.k) link.scaleX = z.k;
    if (link.scaleY !== z.k) link.scaleY = z.k;
    if (link.x !== x) link.x = x;
    if (link.y !== y) link.y = y;
    z.wx = link.x;
    z.wy = link.y;
  }

  /**
   * The window's outline a screen's background shape draws round its panel (WINDOW_OUTLINES) cut off while a wide host
   * shows the screen (the shape shown through a rectangle OUTLINE_CUT px inside its box, moved as far: its panel where
   * the original has it); given back without one, or when the game puts another shape there.
   */
  private cutOutline(z: Zoomed, on: boolean): void {
    const id = WINDOW_OUTLINES[z.name];
    let shape: any = null;
    if (on && id !== undefined) {
      for (let i = 0; i < z.link.numChildren; i++) {
        const c = z.link.getChildAt(i);
        if (c.$charId === id) shape = c;
      }
    }
    const cut = z.cut;
    if (cut && cut.o !== shape) {
      cut.o.scrollRect = null;
      cut.o.x = cut.x;
      cut.o.y = cut.y;
      delete cut.o.$wideOutlineCut;
      z.cut = null;
    }
    if (!shape || z.cut) return;
    const b = shape.getBounds(shape);
    z.cut = { o: shape, x: shape.x, y: shape.y };
    shape.$wideOutlineCut = true;
    shape.scrollRect = new Rectangle(b.x + OUTLINE_CUT, b.y + OUTLINE_CUT, b.width - 2 * OUTLINE_CUT, b.height - 2 * OUTLINE_CUT);
    shape.x = z.cut.x + (b.x + OUTLINE_CUT) * shape.scaleX;
    shape.y = z.cut.y + (b.y + OUTLINE_CUT) * shape.scaleY;
  }

  /**
   * The HUD groups of the screens under the open popups that a popup covers whole are hidden (those its tutorial page
   * is about stay: nothing of them peeks through the popup's gaps), and shown again once no popup covers them; a group
   * it covers only in part stays (the popup drawn over it: the player's plate, the balance beside a larger popup);
   * without a wide host none is hidden.
   */
  private hideHud(open: Array<{ link: any; name: string }>, on: boolean): void {
    const cover: Array<{ r: Area; keep: Set<string> }> = [];
    if (on) {
      for (const z of this.zoomed) {
        if (!z.link.stage || !z.content) continue;
        const b = z.link.getBounds(this.stage);
        const frame = Number(z.link.currentFrame) || 1;
        const keep = new Set(z.name === 'view_tutorial' ? [...(TUTORIAL_PAGES[frame]?.keep ?? []), ...(TUTORIAL_KEEP[frame] ?? [])] : []);
        // (its content's rectangle as shown: not a dim of it over the whole screen)
        const k = z.link.scaleX;
        const c = z.content;
        const r = { x: z.link.x + c.x * k, y: z.link.y + c.y * k, width: c.width * k, height: c.height * k };
        if (b.width > 0) cover.push({ r: inflate(r, 4 / Math.max(0.01, this.stage.cssScale)), keep });
      }
      // The battle's own popups (not zoomed here) cover what they show (not their bounds: the hidden rows of the
      // graves' list counted, a popup parked off the screen).
      for (const o of open) {
        if (!o.link.stage || this.zoomed.some((z) => z.link === o.link) || !battleOwnsPopup(this.fwp, o.name)) continue;
        const r = shownRect(o.link, this.stage);
        if (r && r.width > 0) cover.push({ r: inflate(r, 4 / Math.max(0.01, this.stage.cssScale)), keep: new Set() });
      }
    }
    const hide = new Set<any>();
    const i = this.fwp && this.fwp._interf;
    const c = i && i.cur_screen;
    const n = c ? Number(c[0]) || 0 : 0;
    if (cover.length) {
      for (let k = 1; k <= n; k++) {
        const link = c[k] && c[k].link;
        if (!link || open.some((o) => o.link === link) || typeof link.numChildren !== 'number') continue;
        for (let j = 0; j < link.numChildren; j++) {
          const s = link.getChildAt(j);
          if (typeof s.name !== 'string' || !s.name.startsWith(HUD_PREFIX)) continue;
          const group = s.name.slice(HUD_PREFIX.length);
          if (!s.visible && !this.hiddenHud.has(s)) continue; // hidden by its own layout
          // (bounds count hidden objects too)
          const b = s.getBounds(this.stage);
          if (b.width > 0 && cover.some((p) => !p.keep.has(group) && holds(p.r, b))) hide.add(s);
        }
      }
    }
    for (const s of this.hiddenHud) {
      if (hide.has(s)) continue;
      this.hiddenHud.delete(s);
      if (s.stage) s.visible = true;
    }
    for (const s of hide) {
      this.hiddenHud.add(s);
      if (s.visible) s.visible = false;
    }
  }

  // ---------------------------------------------------------------- the tutorial's dims

  /**
   * The tutorial's page (its frame) on a wide screen: its own dim hidden (page 1-2's flat one; pages 3-6's shape, its
   * panel shown by the extension art), one flat dim over everything shown with the page's spotlights, under the page.
   */
  private dressTutorial(z: Zoomed): void {
    if (z.name !== 'view_tutorial') return;
    const link = z.link;
    const frame = Number(link.currentFrame) || 1;
    const page = TUTORIAL_PAGES[frame];
    if (frame !== z.page) {
      z.page = frame;
      if (z.art && z.art.parent) z.art.parent.removeChild(z.art);
      z.art = null;
      // (the timeline keeps an object at its depth and gives it the next page's character: the panel of the page
      // after a dim hidden here is shown again)
      for (const c of z.dims) c.visible = true;
      z.dims.clear();
      for (let i = 0; i < link.numChildren; i++) {
        const c = link.getChildAt(i);
        if (c.$charId === TUTORIAL_FLAT || (page && c.$charId === page.shape)) {
          const art = page ? this.artObject(page.art) : null;
          if (page && !art) continue; // (no art: the page as the original draws it)
          c.visible = false;
          z.dims.add(c);
          if (art) {
            link.addChildAt(art, link.getChildIndex(c) + 1);
            z.art = art;
          }
        }
      }
      z.dimKey = '';
    }
    if (!z.dim) {
      z.dim = new Sprite();
      z.dim.name = '$wide_tutorial_dim';
      z.dim.mouseEnabled = false;
      z.dim.mouseChildren = false;
    }
    if (z.dim.parent !== link || link.getChildIndex(z.dim) !== 0) link.addChildAt(z.dim, 0);
    // The dim over what is shown, in the tutorial's own coordinates (it is scaled and slides): drawn again when the
    // shown area, its place or the spotlights' objects moved.
    const v = this.stage.visibleRect;
    const plate = page?.plate ? this.hudGroup('plate') : null;
    const pm = plate ? plate.transform.concatenatedMatrix : null;
    const key = `${frame}|${v.x},${v.y},${v.width},${v.height}|${link.scaleX},${link.x},${link.y}|${pm ? [pm.a, pm.tx, pm.ty].join(',') : ''}`;
    if (key === z.dimKey) return;
    z.dimKey = key;
    const local = (x: number, y: number) => link.globalToLocal(new Point(x, y));
    const g = z.dim.graphics;
    g.clear();
    g.beginFill(TUTORIAL_DIM.color, TUTORIAL_DIM.alpha);
    // (a pixel beyond the shown area on each side: its edges never show)
    const a = local(v.x - 1, v.y - 1);
    const b = local(v.x + v.width + 1, v.y + v.height + 1);
    g.drawRect(a.x, a.y, b.x - a.x, b.y - a.y);
    // The spotlights: sub-paths of the same fill (even-odd: holes in it).
    if (page?.plate && plate) {
      for (const r of page.plate) {
        const p0 = local(...toStage(plate, r.x, r.y));
        const p1 = local(...toStage(plate, r.x + r.width, r.y + r.height));
        g.drawRect(Math.min(p0.x, p1.x), Math.min(p0.y, p1.y), Math.abs(p1.x - p0.x), Math.abs(p1.y - p0.y));
      }
    }
    if (page?.path) drawPath(g, page.path, local);
    g.endFill();
  }

  /** The tutorial as the original draws it again (the wide view went off). */
  private static undressTutorial(z: Zoomed): void {
    for (const c of z.dims) c.visible = true;
    z.dims.clear();
    for (const o of [z.art, z.dim]) if (o && o.parent) o.parent.removeChild(o);
    z.art = null;
    z.page = 0;
    z.dimKey = '';
  }

  /** A new instance of the extension art `name` (a linkage class of the art's bundles), named as a part, or null. */
  private artObject(name: string): any {
    const bundle = this.art().find((c) => c.has(name));
    const o = bundle ? bundle.get_object(name) : null;
    if (!o || typeof o.getBounds !== 'function') return null;
    o.name = '$wide_' + name;
    if ('mouseEnabled' in o) o.mouseEnabled = false;
    if ('mouseChildren' in o) o.mouseChildren = false;
    return o;
  }

  // ---------------------------------------------------------------- hit areas

  /**
   * Each shown button of the popup whose art measures less than HIT_CSS CSS px takes the taps of a rectangle up to
   * HIT_CSS round it: as far as the other controls of the popup (buttons, text fields one types into, clips with a
   * hand cursor) leave room, half way to a neighbour, within the popup and a little beyond (its close arrow at the
   * frame's corner). Laid out again when its buttons or its scale change.
   */
  private setHits(z: Zoomed): void {
    const link = z.link;
    const css = this.stage.cssScale;
    if (!(css > 0) || !link.stage) return;
    const buttons: any[] = [];
    const others: any[] = [];
    const walk = (o: any, depth: number): void => {
      if (!o || !o.visible) return;
      if (o.$isButton) {
        // (the corner's close arrow has a target of its own: placeCorners)
        if (o.enabled !== false && o.mouseEnabled !== false && !this.corners.has(o)) buttons.push(o);
        return;
      }
      if (o.$renderKind === 'text') {
        if (o.type === 'input') others.push(o);
        return;
      }
      if (o.buttonMode) {
        // A link (a clip with a hand cursor whose children take no taps: the receipt's "Diamonds | Gold") is a button
        // to grow; a clip whose children take the taps is a neighbour.
        if (o.mouseChildren === false && typeof o.numChildren === 'number' && o.mouseEnabled !== false) buttons.push(o);
        else others.push(o);
        return;
      }
      // (not into a list that scrolls under a mask: its rows are no targets to grow)
      if (depth > 3 || o.mask || o.scrollRect || o.mouseChildren === false || typeof o.numChildren !== 'number') return;
      for (let i = 0; i < o.numChildren; i++) walk(o.getChildAt(i), depth + 1);
    };
    for (let i = 0; i < link.numChildren; i++) walk(link.getChildAt(i), 0);
    // (a button's bounds are its shown state's: its art, not its hit area; a link's, its children but the hit area's)
    const art = (o: any): Area | null => {
      let b: any;
      try {
        if (o.$isButton) b = o.getBounds(this.stage);
        else {
          let r: Area | null = null;
          for (let i = 0; i < o.numChildren; i++) {
            const c = o.getChildAt(i);
            if (c.name === LINK_HIT) continue;
            const cb = c.getBounds(this.stage);
            if (cb.width > 0 && cb.height > 0) r = union(r, { x: cb.x, y: cb.y, width: cb.width, height: cb.height });
          }
          b = r;
        }
      } catch {
        return null;
      }
      return b && b.width > 0 && b.height > 0 ? { x: b.x, y: b.y, width: b.width, height: b.height } : null;
    };
    const arts = buttons.map((b) => ({ b, r: art(b) }));
    const key = `${link.scaleX},${link.x},${link.y}|` + arts.map((a) => (a.r ? `${Math.round(a.r.x)},${Math.round(a.r.y)},${Math.round(a.r.width)}` : '-')).join(';');
    if (key === z.hitKey) return;
    z.hitKey = key;
    const T = HIT_CSS / css;
    const gap = HIT_GAP_CSS / css;
    const split = HIT_SPLIT_CSS / css / 2;
    const walls: Area[] = [];
    for (const o of others) {
      const r = art(o);
      if (r) walls.push(r);
    }
    // A popup's room: its content as shown and a margin of half a target round it (the close arrow's corner).
    const c = z.content;
    const k = link.scaleX;
    const room = c ? inflate({ x: link.x + c.x * k, y: link.y + c.y * k, width: c.width * k, height: c.height * k }, T / 2) : null;
    const want = arts.map(({ r }) => {
      if (!r || (r.width >= T && r.height >= T)) return null;
      const w = Math.max(r.width, T);
      const h = Math.max(r.height, T);
      return { x: r.x + r.width / 2 - w / 2, y: r.y + r.height / 2 - h / 2, width: w, height: h };
    });
    const out: Array<Area | null> = want.map((w, i) => {
      if (!w) return null;
      const own = arts[i].r!;
      let x0 = w.x;
      let y0 = w.y;
      let x1 = w.x + w.width;
      let y1 = w.y + w.height;
      // Buttons whose art mostly overlaps this one's (two states of one control at one place) are one target.
      const near = arts.filter((a, j) => j !== i && a.r && !(overlapShare(a.r, own) > 0.5)).map((a, j) => ({ r: a.r!, grown: want[arts.indexOf(a)] }));
      const obstacles = [...walls.map((r) => ({ r, grown: null as Area | null })), ...near];
      for (const { r, grown } of obstacles) {
        const g0 = grown ?? r;
        if (!(x0 < g0.x + g0.width && g0.x < x1 && y0 < g0.y + g0.height && g0.y < y1)) continue;
        // Cut on the side facing it: half way to its art less half the tie band (a neighbour that grows too), else to its art.
        const half = !!grown;
        const cutX = own.x + own.width <= r.x ? 'r' : r.x + r.width <= own.x ? 'l' : null;
        const cutY = own.y + own.height <= r.y ? 'b' : r.y + r.height <= own.y ? 't' : null;
        const lx = cutX === 'r' ? (half ? (own.x + own.width + r.x) / 2 - split : r.x - gap) : cutX === 'l' ? (half ? (r.x + r.width + own.x) / 2 + split : r.x + r.width + gap) : null;
        const ly = cutY === 'b' ? (half ? (own.y + own.height + r.y) / 2 - split : r.y - gap) : cutY === 't' ? (half ? (r.y + r.height + own.y) / 2 + split : r.y + r.height + gap) : null;
        // The cut that keeps more of the target.
        const areaX = lx === null ? -1 : cutX === 'r' ? (Math.max(own.x + own.width, lx) - x0) * (y1 - y0) : (x1 - Math.min(own.x, lx)) * (y1 - y0);
        const areaY = ly === null ? -1 : cutY === 'b' ? (Math.max(own.y + own.height, ly) - y0) * (x1 - x0) : (y1 - Math.min(own.y, ly)) * (x1 - x0);
        if (areaX < 0 && areaY < 0) continue; // (art over art: nothing to cut)
        if (areaX >= areaY) {
          if (cutX === 'r') x1 = Math.max(own.x + own.width, Math.min(x1, lx!));
          else x0 = Math.min(own.x, Math.max(x0, lx!));
        } else if (cutY === 'b') y1 = Math.max(own.y + own.height, Math.min(y1, ly!));
        else y0 = Math.min(own.y, Math.max(y0, ly!));
      }
      if (room) {
        x0 = Math.max(x0, Math.min(own.x, room.x));
        y0 = Math.max(y0, Math.min(own.y, room.y));
        x1 = Math.min(x1, Math.max(own.x + own.width, room.x + room.width));
        y1 = Math.min(y1, Math.max(own.y + own.height, room.y + room.height));
      }
      return { x: x0, y: y0, width: x1 - x0, height: y1 - y0 };
    });
    // Give the buttons their hit states (a transparent rectangle in the button's coordinates); the others their own.
    const keep = new Set<any>();
    arts.forEach(({ b }, i) => {
      const r = out[i];
      if (!r) return;
      keep.add(b);
      if (!z.hits.has(b)) z.hits.set(b, b.$isButton ? b.hitTestState : null);
      const p0 = b.globalToLocal(new Point(r.x, r.y));
      const p1 = b.globalToLocal(new Point(r.x + r.width, r.y + r.height));
      const rect = { x: Math.min(p0.x, p1.x), y: Math.min(p0.y, p1.y), width: Math.abs(p1.x - p0.x), height: Math.abs(p1.y - p0.y) };
      if (!b.$isButton) {
        // A link: a transparent rectangle under its children takes the taps (it keeps them from its children).
        let hit = b.getChildByName(LINK_HIT);
        if (!hit) {
          hit = new Sprite();
          hit.name = LINK_HIT;
          b.addChildAt(hit, 0);
        }
        hit.graphics.clear();
        hit.graphics.beginFill(0, 0);
        hit.graphics.drawRect(rect.x, rect.y, rect.width, rect.height);
        hit.graphics.endFill();
        return;
      }
      const s = new Sprite();
      s.name = '$wide_hit';
      s.graphics.beginFill(0, 0);
      s.graphics.drawRect(rect.x, rect.y, rect.width, rect.height);
      s.graphics.endFill();
      b.hitTestState = s;
    });
    for (const [b, orig] of z.hits) {
      if (keep.has(b)) continue;
      restoreHit(b, orig);
      z.hits.delete(b);
    }
  }
}

/** The hints' text (the hover popup's main_text, px as the original draws it) and the room kept round a finger (CSS px). */
const HINT_PX = 12;
const HINT_MAX = 1.8;
const FINGER_CSS = 30;
const HINT_GAP_CSS = 10;

/**
 * The game's hint (new_interf.popup: a hovered object's help, on a touch screen a finger held on it) on a wide host:
 * shown larger, its text about TEXT_CSS CSS px (the original's 12 px measured 8 CSS px on a phone), placed within the
 * shown safe area beside what it tells about (`anchor`: the HUD group it is in, e.g. the player's plate, or the object
 * itself) and clear of the finger at (x, y) (stage coordinates): right of them, else left, below, above; the original
 * put it 12 px right of and below the pointer, under the finger. Returns false without a wide host (the caller places
 * it as the original did; its scale is 1 again).
 */
export function placeHint(interf: any, popup: any, anchor: any, x: number, y: number): boolean {
  const st = interf && interf.stage;
  const css = st ? st.cssScale : 0;
  if (!st || !st.$visibleArea || !(css > 0) || !popup || !popup.popup_bg) {
    if (popup && popup.scaleX !== 1) popup.scaleX = popup.scaleY = 1;
    return false;
  }
  const k = Math.min(HINT_MAX, Math.max(1, TEXT_CSS / (HINT_PX * css)));
  if (popup.scaleX !== k) popup.scaleX = popup.scaleY = k;
  // (the hint's own coordinates are the interface's: the stage's)
  const bg = popup.popup_bg.getBounds(popup);
  const w = bg.width * k;
  const h = bg.height * k;
  const r = st.safeRect;
  const m = MARGIN_CSS / css;
  const f = FINGER_CSS / css;
  const g = HINT_GAP_CSS / css;
  const finger: Area = { x: x - f, y: y - f, width: 2 * f, height: 2 * f };
  let about: Area | null = null;
  for (let o = anchor; o && o !== st; o = o.parent) {
    if (typeof o.name === 'string' && o.name.startsWith(HUD_PREFIX)) {
      about = boundsOf(o, st);
      break;
    }
  }
  if (!about && anchor && anchor.stage) about = boundsOf(anchor, st) ?? hitBounds(anchor);
  const keep = about && about.width > 0 ? union(inflate(about, g), finger) : finger;
  const clampX = (v: number) => Math.min(Math.max(v, r.x + m), r.x + r.width - m - w);
  const clampY = (v: number) => Math.min(Math.max(v, r.y + m), r.y + r.height - m - h);
  const tries: Area[] = [
    { x: keep.x + keep.width, y: clampY(keep.y), width: w, height: h },
    { x: keep.x - w, y: clampY(keep.y), width: w, height: h },
    { x: clampX(x - w / 2), y: keep.y + keep.height, width: w, height: h },
    { x: clampX(x - w / 2), y: keep.y - h, width: w, height: h },
  ];
  const inside = (t: Area) => t.x >= r.x + m - 0.5 && t.y >= r.y + m - 0.5 && t.x + t.width <= r.x + r.width - m + 0.5 && t.y + t.height <= r.y + r.height - m + 0.5;
  const at = tries.find(inside) ?? { x: clampX(tries[0].x), y: clampY(tries[0].y), width: w, height: h };
  popup.x = at.x - bg.x * k;
  popup.y = at.y - bg.y * k;
  return true;
}

/** A button's hit area (a button with no art of its own: only its hit state) in stage coordinates, or null. */
function hitBounds(b: any): Area | null {
  const hs = b && b.$isButton ? b.hitTestState : null;
  if (!hs) return null;
  try {
    const l = hs.getBounds(hs);
    if (!(l.width > 0 && l.height > 0)) return null;
    const p0 = b.localToGlobal(new Point(l.x, l.y));
    const p1 = b.localToGlobal(new Point(l.x + l.width, l.y + l.height));
    return { x: Math.min(p0.x, p1.x), y: Math.min(p0.y, p1.y), width: Math.abs(p1.x - p0.x), height: Math.abs(p1.y - p0.y) };
  } catch {
    return null;
  }
}

function boundsOf(o: any, st: any): Area | null {
  try {
    const b = o.getBounds(st);
    return b.width > 0 && b.height > 0 ? { x: b.x, y: b.y, width: b.width, height: b.height } : null;
  } catch {
    return null;
  }
}

/** A button's own hit state again (a link: its grown hit area removed). */
function restoreHit(b: any, orig: any): void {
  if (b.$isButton) b.hitTestState = orig;
  else {
    const hit = b.getChildByName(LINK_HIT);
    if (hit) b.removeChild(hit);
  }
}

/** The share of the smaller rectangle that two rectangles share. */
function overlapShare(a: Area, b: Area): number {
  const w = Math.min(a.x + a.width, b.x + b.width) - Math.max(a.x, b.x);
  const h = Math.min(a.y + a.height, b.y + b.height) - Math.max(a.y, b.y);
  if (!(w > 0 && h > 0)) return 0;
  return (w * h) / Math.min(a.width * a.height, b.width * b.height);
}

/** A point of `sprite`'s coordinates in stage coordinates. */
function toStage(sprite: any, x: number, y: number): [number, number] {
  const p = sprite.localToGlobal(new Point(x, y));
  return [p.x, p.y];
}

/** The numbers of an 'M x y Q cx cy x y ... Z' path. */
function pathNumbers(d: string): Array<{ cmd: string; v: number[] }> {
  const out: Array<{ cmd: string; v: number[] }> = [];
  const re = /([MQLZ])([^MQLZ]*)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(d))) out.push({ cmd: m[1], v: m[2].trim() ? m[2].trim().split(/\s+/).map(Number) : [] });
  return out;
}

/** The bounds of a path's points (its control points too: a little more than its outline). */
function pathBounds(d: string): Area {
  let r: Area | null = null;
  for (const { v } of pathNumbers(d)) for (let i = 0; i + 1 < v.length; i += 2) r = union(r, { x: v[i], y: v[i + 1], width: 0, height: 0 });
  return r ?? { x: 0, y: 0, width: 0, height: 0 };
}

/** Draws a path given in stage coordinates into `g` (in the coordinates `local` maps stage points to). */
function drawPath(g: any, d: string, local: (x: number, y: number) => { x: number; y: number }): void {
  for (const { cmd, v } of pathNumbers(d)) {
    if (cmd === 'M') {
      const p = local(v[0], v[1]);
      g.moveTo(p.x, p.y);
    } else if (cmd === 'L') {
      const p = local(v[0], v[1]);
      g.lineTo(p.x, p.y);
    } else if (cmd === 'Q') {
      for (let i = 0; i + 3 < v.length; i += 4) {
        const c = local(v[i], v[i + 1]);
        const p = local(v[i + 2], v[i + 3]);
        g.curveTo(c.x, c.y, p.x, p.y);
      }
    }
  }
}
